import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import MessagePlaces from '@/components/Messages/MessagePlaces';
import MapPlaceCard from '@/components/Maps/MapPlaceCard';
import PlaceDetailsPanel from '@/components/Maps/PlaceDetailsPanel';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import { mapsApi } from '@/lib/api/maps';
import { MAP_IDEAS, mapsChatPrompt, requestMapsLocation } from '@/lib/mapsDiscovery';
import { openIdeaComposer } from '@/lib/openIdeaComposer';

const SHORTCUTS = [
  ['Restaurants', 'utensils', 'Great restaurants'],
  ['Coffee', 'coffee', 'Great coffee shops'],
  ['Outdoors', 'tree-large', 'Parks and walking trails'],
  ['With the kids', 'compass', 'Fun family attractions and things to do with kids'],
];

export default function MapsPage() {
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const { isXS, isSM } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const requireAuth = useRequireAuth();
  const userId = useAuthStore(state => state.user?.id);
  const memoryAllowed = useAuthStore(state => state.user?.preferences?.memory?.isEnabled !== false);
  const memoryEnabled = !!userId && memoryAllowed;
  const memoryEnabledRef = useRef(memoryEnabled);
  memoryEnabledRef.current = memoryEnabled;
  const [profile, setProfile] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestionError, setSuggestionError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [planning, setPlanning] = useState(false);
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [approximateArea, setApproximateArea] = useState('');
  const [findingApproximate, setFindingApproximate] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [locating, setLocating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [contentWidth, setContentWidth] = useState(0);
  const request = useRef(0);
  const locationRequest = useRef(0);
  const locationInput = useRef(null);
  const searchInput = useRef(null);
  const planRequest = useRef(0);
  const queryRef = useRef(query);
  queryRef.current = query;
  const canLocate = Platform.OS === 'web' && typeof navigator !== 'undefined' && !!navigator.geolocation;
  const columns = contentWidth ? (contentWidth >= 680 ? 3 : contentWidth >= 440 ? 2 : 1) : (isXS ? 1 : 3);
  const cardWidth = contentWidth ? (contentWidth - (columns - 1) * 16) / columns : `${100 / columns - 2}%`;
  useEffect(() => { setSelectedPlace(null); }, [result, userId]);

  // Coordinates and results are session-only and never survive an account change.
  useEffect(() => {
    request.current += 1;
    locationRequest.current += 1;
    planRequest.current += 1;
    setProfile(null); setRecommendations(null); setPlanning(false);
    setResult(null); setCoordinates(null); setApproximateArea(''); setFindingApproximate(false); setLocation(''); setQuery('');
    setLoading(false); setLocating(false); setError(''); setShowIntro(true);
    return () => { request.current += 1; locationRequest.current += 1; planRequest.current += 1; };
  }, [userId]);

  useEffect(() => {
    let current = true;
    setProfile(null); setRecommendations(null);
    if (memoryEnabled) mapsApi.preferences(true, () => current).then(data => { if (current) setProfile(data); }).catch(() => { if (current) setProfile({ status: 'unavailable', interests: [] }); });
    return () => { current = false; };
  }, [userId, memoryEnabled, refresh]);

  useEffect(() => {
    let current = true;
    setRecommendations(null); setSuggestionError(''); setSuggesting(false);
    if (!userId || (memoryEnabled && !profile) || locating || (!location.trim() && !coordinates)) return () => { current = false; };
    setSuggesting(true);
    const timer = setTimeout(async () => {
      try {
        const data = await mapsApi.recommendations({ location: location.trim(), coordinates, useMemory: memoryEnabled }, () => current);
        if (current) setRecommendations(approximateArea ? { ...data, location: approximateArea, approximate: true } : data);
      } catch (_) { if (current) setSuggestionError('Suggestions are unavailable right now. Search above or try again.'); }
      finally { if (current) setSuggesting(false); }
    }, 650);
    return () => { current = false; clearTimeout(timer); };
  }, [userId, memoryEnabled, profile, location, coordinates, approximateArea, locating, refresh]);

  // Account-level opt-out also removes any previously personalized screen state.
  useEffect(() => {
    if (!memoryAllowed) { request.current += 1; planRequest.current += 1; setResult(null); setSelectedPlace(null); setLoading(false); setPlanning(false); }
  }, [memoryAllowed]);

  async function planOuting(places) {
    if (planning) return;
    const id = ++planRequest.current;
    setPlanning(true);
    try {
      // Reload preferences at the moment of planning so edits/deletions in
      // Settings cannot survive as stale preference text in a new chat.
      const fresh = memoryEnabled ? await mapsApi.preferences(true, () => id === planRequest.current) : null;
      if (id !== planRequest.current) return;
      const personalize = memoryEnabled && fresh?.status !== 'disabled';
      openIdeaComposer(router, { mode: 'chat', autoMode: true, prompt: mapsChatPrompt(places, { useMemory: personalize, interests: fresh?.interests || [] }) });
    } catch (_) {
      if (id === planRequest.current) setError('Could not prepare your outing. Please try again.');
    } finally { if (id === planRequest.current) setPlanning(false); }
  }

  function changeLocation(value) {
    setSelectedPlace(null);
    planRequest.current += 1; setPlanning(false);
    locationRequest.current += 1;
    request.current += 1;
    setLocating(false); setFindingApproximate(false); setLoading(false); setCoordinates(null); setApproximateArea(''); setLocation(value);
    setResult(null); setError('');
  }

  async function search(text = query, override, personalize = memoryEnabledRef.current) {
    const nextQuery = text.trim();
    if (!nextQuery) { searchInput.current?.focus(); return; }
    if (!override) { locationRequest.current += 1; setLocating(false); }
    const area = override || { location: location.trim(), coordinates, approximateArea };
    setQuery(nextQuery);
    if (!area.location && !area.coordinates) {
      setError('Choose an area first. Enter a city or address, or use your location.');
      locationInput.current?.focus();
      return;
    }
    if (requireAuth(() => true, { source: 'maps_search', title: 'Sign in to discover places', icon: 'map' }) !== true) return;
    const id = ++request.current;
    planRequest.current += 1; setPlanning(false);
    setLoading(true); setError(''); setResult(null);
    try {
      const data = await mapsApi.search({ query: nextQuery, location: area.location, coordinates: area.coordinates, useMemory: personalize }, () => id === request.current);
      if (id === request.current) { setResult(area.approximateArea ? { ...data, location: area.approximateArea, approximate: true } : data); setShowIntro(false); }
    } catch (failure) {
      if (id === request.current) setError(failure.message || 'Unable to find places. Please try again.');
    } finally {
      if (id === request.current) setLoading(false);
    }
  }

  async function locate() {
    if (requireAuth(() => true, { source: 'maps_location', title: 'Sign in to discover nearby places', icon: 'map' }) !== true) return;
    const id = ++locationRequest.current;
    request.current += 1;
    setLoading(false); setLocating(true); setFindingApproximate(false); setError(''); setResult(null);
    try {
      let value, area = '';
      try {
        value = await requestMapsLocation();
      } catch (failure) {
        if (id !== locationRequest.current) return;
        // Respect a denied permission. Only provider failures/timeouts use the
        // network fallback, and a typed destination cancels it immediately.
        if (failure.code !== 2 && failure.code !== 3) throw failure;
        setFindingApproximate(true);
        const fallback = await mapsApi.approximateLocation(() => id === locationRequest.current);
        value = fallback.coordinates;
        area = fallback.area;
      }
      if (id !== locationRequest.current) return;
      setCoordinates(value); setLocation(''); setApproximateArea(area);
      search(queryRef.current || 'Restaurants, coffee shops and parks', { location: '', coordinates: value, approximateArea: area });
    } catch (failure) {
      if (id === locationRequest.current) { setError(failure.code === 1 ? failure.message : 'We could not determine your area. Enter a city or address to find places.'); locationInput.current?.focus(); }
    } finally {
      if (id === locationRequest.current) { setLocating(false); setFindingApproximate(false); }
    }
  }

  const button = (label, onPress, icon, primary = false, disabled = false) => (
    <Pressable accessibilityRole="button" accessibilityLabel={label} accessibilityState={{ disabled }} disabled={disabled}
      onPress={onPress} style={({ hovered, pressed }) => [styles.button, { backgroundColor: primary ? palette.ink : (hovered || pressed ? palette.hover : palette.control), borderColor: primary ? palette.ink : palette.border, opacity: disabled ? 0.5 : 1 }]}>
      {icon && <Icon name={icon} size={14} color={primary ? palette.onInk : palette.ink} />}
      <Text size="s" weight="medium" style={{ color: primary ? palette.onInk : palette.ink }}>{label}</Text>
    </Pressable>
  );

  return (
    <View style={[styles.page, { backgroundColor: colors.solid_color_primary }]}>
      <MetaHead title="Maps" description="Discover places, find a new favorite, and plan your next outing with Gab AI." noindex />
      <ScrollView style={{ flex: 1, minWidth: 0 }} keyboardShouldPersistTaps="handled" contentContainerStyle={{ paddingBottom: insets.bottom + 64 }}>
        <View style={[styles.content, { paddingHorizontal: isXS ? 20 : 36, paddingTop: isXS ? 24 : 40 }]}>
          <View style={styles.heading}>
            <View><Text tag="h1" accessibilityRole="header" weight="semibold" style={styles.title}>Maps</Text>
              <Text color="secondary" style={styles.subtitle}>A little closer to your next favorite place.</Text></View>
            {!!result && button('New search', () => { request.current += 1; setResult(null); setQuery(''); setError(''); searchInput.current?.focus(); }, 'plus')}
          </View>

          <View style={[styles.search, { backgroundColor: palette.preview, borderColor: palette.border }]}>
            <Icon name="magnifying-glass" size={20} color={palette.icon} />
            <TextInput ref={searchInput} accessibilityLabel="Search places" placeholder="Ask Gab AI what’s nearby" value={query}
              onChangeText={setQuery} onSubmitEditing={() => search()} returnKeyType="search" maxLength={500}
              placeholderTextColor={colors.text_color_secondary} style={[styles.searchInput, { color: palette.ink }]}
              />
            <Pressable accessibilityRole="button" accessibilityLabel="Find places" disabled={!query.trim() || loading || locating}
              accessibilityState={{ disabled: !query.trim() || loading || locating }} onPress={() => search()}
              style={[styles.send, { backgroundColor: palette.ink, opacity: !query.trim() || loading || locating ? 0.4 : 1 }]}>
              <Icon name="arrow-up" size={19} color={palette.onInk} />
            </Pressable>
          </View>

          <View style={styles.areaRow}>
            <View style={[styles.areaInput, { borderColor: palette.border }]}>
              <Icon name="location-dot" size={14} color={palette.icon} />
              {coordinates ? <View style={styles.currentArea}>
                <Text size="s" style={{ flex: 1 }}>{approximateArea ? `Approximate area: ${approximateArea}` : 'Using your current area'}</Text>
                <Pressable accessibilityRole="button" accessibilityLabel="Stop using my location" onPress={() => { changeLocation(''); locationInput.current?.focus(); }} style={styles.clear}>
                  <Icon name="xmark" size={13} color={palette.icon} />
                </Pressable>
              </View> : <TextInput ref={locationInput} accessibilityLabel="City or address" placeholder="City, neighborhood, or address" value={location}
                onChangeText={changeLocation} maxLength={200} returnKeyType="search" onSubmitEditing={() => search()}
                placeholderTextColor={colors.text_color_secondary} style={[styles.locationInput, { color: palette.ink }]} />}
            </View>
            {canLocate && button(locating ? (findingApproximate ? 'Finding approximate area…' : 'Finding location…') : 'Use my location', locate, 'location-arrow', false, locating)}
          </View>
          {!!approximateArea && <Text size="xs" color="secondary" style={{ marginTop: 8 }}>Your browser couldn’t provide a location, so we’re using your network’s approximate area. Clear it to choose another city.</Text>}
          <View style={styles.shortcuts}>
            {SHORTCUTS.map(([label, icon, text]) => <React.Fragment key={label}>{button(label, () => search(text), icon)}</React.Fragment>)}
          </View>

          {!!error && <View accessibilityRole="alert" style={[styles.notice, { borderColor: palette.border, backgroundColor: palette.surface }]}>
            <Icon name="circle-info" size={18} color={palette.icon} /><Text size="s" style={{ flex: 1 }}>{error}</Text>
          </View>}

          {loading ? <View accessibilityRole="progressbar" accessibilityLabel="Finding places" style={[styles.loading, { backgroundColor: palette.surface, borderColor: palette.border }]}>
            <ActivityIndicator color={palette.ink} /><Text weight="medium">Finding your next favorite…</Text><Text size="s" color="secondary">Looking for places {location ? `near ${location}` : 'in your area'}</Text>
          </View> : result ? <View style={styles.results}>
            <View style={styles.resultsHeading}><Text tag="h2" accessibilityRole="header" weight="semibold" style={styles.sectionTitle}>{result.query}</Text>
              <Text size="s" color="secondary">{result.approximate ? 'Approximate area: ' : ''}{result.location} · {result.places.length} {result.places.length === 1 ? 'place' : 'places'}</Text></View>
            {result.places.length > 0 ? <>
              <View style={[styles.chatCta, { borderColor: palette.border }]}>
                <View style={{ flex: 1, minWidth: 160, gap: 4 }}><Text weight="semibold">Turn a few places into a plan.</Text><Text size="s" color="secondary">{planning ? 'Preparing your plan…' : memoryEnabled && profile?.status === 'ready' ? 'An itinerary shaped around your saved preferences.' : 'Get an itinerary with stops, timing, and map links.'}</Text></View>
                {button('Plan an outing', () => planOuting(result), 'map', true, planning)}
              </View>
              <MessagePlaces key={request.current} places={result.places} map={result.map} presentation="discovery" onPlacePress={setSelectedPlace} activePlace={selectedPlace} />
            </> : <View style={[styles.empty, { backgroundColor: palette.surface }]}>
              <Icon name="map" size={28} color={palette.icon} /><Text weight="semibold">No places found in this area</Text>
              <Text color="secondary" style={styles.centered}>Try a broader search or a nearby city.</Text>
            </View>}
          </View> : showIntro && !location.trim() && !coordinates && !(memoryEnabled && profile?.status === 'ready') && <View style={[styles.intro, { backgroundColor: palette.surface, borderColor: palette.border }]}>
            <Pressable accessibilityRole="button" accessibilityLabel="Dismiss location introduction" onPress={() => setShowIntro(false)} style={[styles.dismiss, { backgroundColor: palette.preview }]}>
              <Icon name="xmark" size={16} color={palette.icon} />
            </Pressable>
            <View style={[styles.heroIcon, { backgroundColor: palette.preview }]}><Icon name="map-location-dot" size={36} color={palette.ink} /></View>
            <Text tag="h2" accessibilityRole="header" weight="semibold" style={[styles.introTitle, { fontSize: isXS ? 25 : 30 }]}>Go somewhere good.</Text>
            <Text color="secondary" style={styles.introCopy}>The perfect coffee, a new trail, a day out with the kids. Discover what’s around you, or explore somewhere new.</Text>
            {canLocate ? button('Use my location', locate, 'location-arrow', true, locating) : button('Choose an area', () => locationInput.current?.focus(), 'location-dot', true)}
            <Text size="xs" color="tertiary" style={styles.privacy}>{canLocate ? 'Location is optional. If your browser cannot locate you, we’ll try your network’s approximate area.' : 'Enter a city or address above to start exploring.'}</Text>
          </View>}

          <View style={[styles.discovery, result && { marginTop: 24 }]} onLayout={event => setContentWidth(event.nativeEvent.layout.width)}>
            <View style={styles.resultsHeading}>
              <Text tag="h2" accessibilityRole="header" weight="semibold" style={styles.sectionTitle}>{memoryEnabled && (!profile || profile.status === 'ready') ? 'Picked for you' : 'Explore nearby'}</Text>
              <Text size="s" color="secondary">{location.trim() || coordinates
                ? (memoryEnabled && profile?.status === 'ready' ? 'Places that match your interests in this area.' : 'Discover restaurants, coffee shops, and places to explore in this area.')
                : 'Choose an area above to discover your next favorite place.'}</Text>
            </View>
            {memoryEnabled && profile?.status === 'ready' && <View style={styles.shortcuts}>{profile.interests.map(interest => <React.Fragment key={interest.id}>{button(interest.label, () => search(interest.query), 'sparkles')}</React.Fragment>)}</View>}
            {(suggesting || locating || (memoryEnabled && !profile && (location.trim() || coordinates))) && <View accessibilityRole="progressbar" accessibilityLabel="Finding suggested places" style={styles.suggesting}><ActivityIndicator color={palette.icon} /><Text size="s" color="secondary">Finding places for you…</Text></View>}
            {!!suggestionError && <Text accessibilityRole="alert" size="s" color="secondary" style={{ marginTop: 16 }}>{suggestionError}</Text>}
            {!!recommendations?.places.length && <>
              {!result && <View style={[styles.chatCta, { borderColor: palette.border, marginBottom: 20 }]}><View style={{ flex: 1, minWidth: 160, gap: 4 }}><Text weight="semibold">Make a day of it.</Text><Text size="s" color="secondary">{planning ? 'Preparing your plan…' : 'An itinerary with stops, timing, and map links.'}</Text></View>{button('Plan an outing', () => planOuting(recommendations), 'map', true, planning)}</View>}
              <View style={[styles.grid, { marginTop: 16 }]}>{recommendations.places.slice(0, result ? 3 : 9).map((place, index) => <MapPlaceCard key={place.dataId || place.placeId || `${place.title}-${index}`} place={place} index={index} width={cardWidth} palette={palette} onPress={setSelectedPlace} />)}</View>
            </>}
            {recommendations && !recommendations.places.length && <Text size="s" color="secondary" style={{ marginTop: 16 }}>No suggestions found here yet. Try a nearby city or search above.</Text>}
            {!!userId && (location.trim() || coordinates) && <View style={{ alignItems: 'flex-start', marginTop: 16 }}>{button('Refresh suggestions', () => setRefresh(value => value + 1), 'rotate-right', false, suggesting)}</View>}
            {!location.trim() && !coordinates && <View style={{ marginTop: 20 }}>
              <View style={styles.grid}>
                {MAP_IDEAS.map(idea => <Pressable key={idea.title} accessibilityRole="button" accessibilityLabel={idea.title}
                  onPress={() => search(idea.query)} style={({ hovered, pressed }) => [styles.idea, { width: cardWidth, backgroundColor: palette.preview, opacity: pressed ? 0.85 : 1, transform: [{ scale: hovered ? 1.015 : 1 }] }]}>
                  <View style={styles.imageFallback}><Icon name={idea.icon} size={44} color={palette.icon} /></View>
                  <Image source={{ uri: idea.image }} style={StyleSheet.absoluteFillObject} contentFit="cover" />
                  <LinearGradient colors={['transparent', 'rgba(0,0,0,0.82)']} style={StyleSheet.absoluteFillObject} />
                  <View style={styles.ideaText}><Text style={styles.eyebrow}>{idea.label}</Text><Text weight="semibold" style={styles.ideaTitle}>{idea.title}</Text></View>
                  <View style={styles.ideaArrow}><Icon name="arrow-up-right" size={15} color="#fff" /></View>
                </Pressable>)}
              </View>
            </View>}
          </View>

          {!loading && !!result?.places.length && <View style={styles.discovery}>
            <Text tag="h2" accessibilityRole="header" weight="semibold" style={styles.sectionTitle}>{result.approximate ? `Places around ${result.location}` : result.location === 'Your current area' ? 'Places near you' : `Places in ${result.location}`}</Text>
            <Text size="s" color="secondary" style={{ marginTop: 6, marginBottom: 20 }}>{memoryEnabled && result.personalization?.ranked ? 'Matches for your saved preferences appear first.' : 'Explore the places from your search.'}</Text>
            <View style={styles.grid}>{result.places.map((place, index) => <MapPlaceCard key={`${request.current}-${place.placeId || index}`} place={place} index={index} width={cardWidth} palette={palette} onPress={setSelectedPlace} />)}</View>
          </View>}
        </View>
      </ScrollView>
      {selectedPlace && <PlaceDetailsPanel key={`${userId}-${selectedPlace.dataId || selectedPlace.placeId || selectedPlace.title}`} place={selectedPlace} compact={isSM} onClose={() => setSelectedPlace(null)} />}
    </View>
  );
}

const styles = StyleSheet.create({
  page: { flex: 1, flexDirection: 'row' },
  content: { width: '100%', maxWidth: 1160, alignSelf: 'center' },
  heading: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 },
  title: { fontSize: 36, lineHeight: 44, letterSpacing: -1.1 },
  subtitle: { marginTop: 6, fontSize: 15, lineHeight: 22 },
  search: { flexDirection: 'row', alignItems: 'center', gap: 14, padding: 10, paddingLeft: 20, borderWidth: 1, borderRadius: 32 },
  searchInput: { flex: 1, minWidth: 0, fontSize: 17, paddingVertical: 12 },
  send: { width: 42, height: 42, borderRadius: 21, alignItems: 'center', justifyContent: 'center' },
  areaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 14 },
  areaInput: { flexGrow: 1, flexBasis: 230, minHeight: 44, flexDirection: 'row', alignItems: 'center', gap: 9, paddingLeft: 14, paddingRight: 8, borderWidth: 1, borderRadius: 14 },
  locationInput: { flex: 1, minWidth: 0, fontSize: 14, paddingVertical: 12 },
  currentArea: { flex: 1, flexDirection: 'row', alignItems: 'center' },
  clear: { width: 32, height: 36, alignItems: 'center', justifyContent: 'center' },
  shortcuts: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 18 },
  suggesting: { flexDirection: 'row', gap: 10, alignItems: 'center', paddingVertical: 24 },
  button: { minHeight: 42, paddingHorizontal: 16, paddingVertical: 10, borderRadius: 24, borderWidth: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  notice: { flexDirection: 'row', alignItems: 'center', gap: 12, borderWidth: 1, borderRadius: 16, padding: 18, marginTop: 24 },
  loading: { minHeight: 360, alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 32, borderRadius: 26, borderWidth: 1, padding: 24 },
  intro: { minHeight: 370, paddingHorizontal: 28, paddingVertical: 48, alignItems: 'center', justifyContent: 'center', marginTop: 32, borderWidth: 1, borderRadius: 26 },
  dismiss: { position: 'absolute', top: 16, right: 16, width: 34, height: 34, alignItems: 'center', justifyContent: 'center', borderRadius: 17 },
  heroIcon: { height: 76, width: 76, borderRadius: 24, alignItems: 'center', justifyContent: 'center', marginBottom: 22 },
  introTitle: { lineHeight: 38, textAlign: 'center', letterSpacing: -0.7 },
  introCopy: { textAlign: 'center', maxWidth: 480, fontSize: 16, lineHeight: 26, marginTop: 12, marginBottom: 24 },
  privacy: { textAlign: 'center', marginTop: 16, maxWidth: 350, lineHeight: 18 },
  results: { marginTop: 30 },
  resultsHeading: { gap: 6 },
  sectionTitle: { fontSize: 22, lineHeight: 29, letterSpacing: -0.5 },
  chatCta: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12, paddingTop: 12, marginTop: 12, borderTopWidth: 1 },
  empty: { padding: 40, borderRadius: 24, gap: 14, alignItems: 'center', marginTop: 20 },
  centered: { textAlign: 'center' },
  discovery: { marginTop: 40 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  idea: { height: 280, borderRadius: 22, overflow: 'hidden', justifyContent: 'flex-end' },
  imageFallback: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center' },
  ideaText: { padding: 22, paddingRight: 42, gap: 8 },
  eyebrow: { fontSize: 10, letterSpacing: 1.4, color: '#eee' },
  ideaTitle: { color: '#fff', fontSize: 22, lineHeight: 27, letterSpacing: -0.5 },
  ideaArrow: { position: 'absolute', right: 18, bottom: 26 },
});
